/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDraggable } from 'react-use-draggable-scroll';
import isDesktop from 'store/app/selector/isDesktop';
import useResizedElementWidth from 'hooks/useResizedElementWidth';
import cn from 'classnames';
import { throttle } from 'lodash';
import { MediumBlockGroupType, Theme } from 'store/landing/types';
import StudioArrow from '../Carousel/StudioRightArrow/StudioArrow';
import FilledArrow from '../Carousel/FilledArrow/FilledArrow';
import styles from './ProductCarousel.module.scss';
import OpText from '../op-ui-wrappers/OpText/OpText';
import ProgressBar from '../ProgressBar/ProgressBar';

type ArrowType = 'studio' | 'filled';

interface OwnProps {
  forceShowArrows?: boolean;
  theme?: Theme;
  arrowType?: ArrowType;
  title?: string;
  progressBar?: boolean;
  scrollable?: boolean;
  type?: 'default' | 'portrait' | 'portrait-grid' | 'portrait-rounded' | 'square';
}

const ProductCarousel: FC<OwnProps> = ({
  forceShowArrows = false,
  arrowType = 'studio',
  theme,
  title,
  type = 'default',
  progressBar,
  scrollable = true,
  children,
}) => {
  const ref = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  const { events } = useDraggable(ref);
  const [maxHorizontalScroll, setMaxHorizontalScroll] = useState(0);
  const maxFixedSize = useResizedElementWidth(ref);
  const isDesktopDevice = useSelector(isDesktop);
  const showArrows = (isDesktopDevice || forceShowArrows) && scrollable;
  const [scrollPosition, setScrollPosition] = useState(0);
  const [carouselItems, setCarouselItems] = useState(6);
  const isAtLeft = scrollPosition <= 0;
  const isAtRight = scrollPosition + 1 >= maxHorizontalScroll;

  useEffect(() => {
    if (ref.current) {
      if (!scrollable) {
        ref.current.style.overflow = 'hidden';
      } else {
        ref.current.style.overflow = 'auto';
      }
    }
  }, [scrollable]);

  useEffect(() => {
    if (scrollable && ref.current && maxHorizontalScroll !== ref.current.scrollWidth - ref.current.clientWidth) {
      setMaxHorizontalScroll(ref.current.scrollWidth - ref.current.clientWidth);
    }
  }, [maxFixedSize, scrollable]);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined' && scrollable) {
      const updateCarouselItems = () => {
        const _styles = window.getComputedStyle(document.documentElement);
        setCarouselItems(parseFloat(_styles.getPropertyValue('--carousel-items')));
      };

      // Update carousel items on initial render and when the window is resized
      updateCarouselItems();
      window.addEventListener('resize', updateCarouselItems);

      // Clean up the event listener when the component is unmounted
      return () => {
        window.removeEventListener('resize', updateCarouselItems);
      };
    }

    // Return a no-op function when the condition is not met
    return () => {};
  }, [scrollable]);

  useEffect(() => {
    if (ref.current) {
      ref.current.childNodes.forEach((child) => child.addEventListener('mousedown', (e) => e.preventDefault()));
    }

    return () => {
      if (ref.current) {
        ref.current.childNodes.forEach((child) => child.removeEventListener('mousedown', (e) => e.preventDefault()));
      }
    };
  }, []);

  const getNewActiveDotIndex = (newScrollLeft: number) => {
    const numberOfDots = Math.ceil(React.Children.toArray(children).length / carouselItems);
    const newActiveDotIndex = Math.round((newScrollLeft * (numberOfDots - 1)) / maxHorizontalScroll);
    return newActiveDotIndex;
  };

  const scrollTo = (left: number) =>
    ref.current.scrollTo({
      behavior: 'smooth',
      left,
    });

  const activeDotIndex = getNewActiveDotIndex(scrollPosition);

  const onLeftArrowHandler = () => {
    const numberOfDots = Math.ceil(React.Children.toArray(children).length / carouselItems);
    const newActiveDotIndex = activeDotIndex - 1;

    // Check if the newActiveDotIndex is within the valid range
    if (newActiveDotIndex >= 0) {
      const targetScrollLeft = (newActiveDotIndex * maxHorizontalScroll) / (numberOfDots - 1);
      scrollTo(targetScrollLeft);
    } else {
      scrollTo(0);
    }
  };

  const handleScroll = throttle((event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    setScrollPosition(event.currentTarget.scrollLeft);
  }, 100);

  const onRightArrowHandler = () => {
    const numberOfDots = Math.ceil(React.Children.toArray(children).length / carouselItems);
    const newActiveDotIndex = activeDotIndex + 1;

    // Check if the newActiveDotIndex is within the valid range
    if (newActiveDotIndex <= numberOfDots) {
      const targetScrollLeft = (newActiveDotIndex * maxHorizontalScroll) / (numberOfDots - 1);
      scrollTo(targetScrollLeft);
    }
  };

  const handleDotClick = (index: number) => {
    const numberOfDots = Math.ceil(React.Children.toArray(children).length / carouselItems);

    const targetScrollLeft = (index * maxHorizontalScroll) / (numberOfDots - 1);
    scrollTo(targetScrollLeft);
  };

  return (
    <div className={cn(styles.container, styles[`theme-${theme}`], styles[type])}>
      {title && (
        <div className={styles.header}>
          {title && <OpText variant="title-large-bold">{title}</OpText>}
          {progressBar && isDesktopDevice && (
            <div className={styles.progressBar}>
              <ProgressBar
                activeDotIndex={activeDotIndex}
                onClick={handleDotClick}
                elementsDisplayed={carouselItems}
                totalElements={React.Children.toArray(children).length}
              />
            </div>
          )}
        </div>
      )}
      <div className={styles.carousel}>
        {showArrows && !isAtLeft && (
          <>
            {arrowType === 'studio' && (
              <StudioArrow direction="left" className={styles.left} onClick={() => onLeftArrowHandler()} />
            )}
            {arrowType === 'filled' && (
              <FilledArrow direction="left" className={styles.left} onClick={() => onLeftArrowHandler()} />
            )}
          </>
        )}
        <div
          ref={ref}
          className={styles.innerContainer}
          {...(scrollable && events)}
          onScroll={handleScroll}
          onLoad={() => {
            setMaxHorizontalScroll(ref.current.scrollWidth - ref.current.clientWidth);
          }}
        >
          {children}
        </div>
        {showArrows && !isAtRight && (
          <>
            {arrowType === 'studio' && (
              <StudioArrow direction="right" className={styles.right} onClick={() => onRightArrowHandler()} />
            )}
            {arrowType === 'filled' && (
              <FilledArrow direction="right" className={styles.right} onClick={() => onRightArrowHandler()} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductCarousel;
